import { StyleSheet, Modal, Alert } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import React, { useState, useEffect, memo, useMemo } from "react";
import t from "@constants/localization";
import RTOFeedbackModal from "./RTOFeedbackModal";
import { confirmOrderDeliveryFlow } from "@services/api";
import InAppRTOModal from "./InAppRTOModal";
import { actions as snackbarActions } from "@reducers/snackbarReducer";
import { actions as delayDataActions } from "@reducers/delayDataReducer";
import analytics from "@services/analytics";
import { DELIVERY_COMMUNICATION_EVENTS } from "@constants/analyticsEventsCT";

const deliveryFlowInfoSelector = (state) => state?.delayedDataReducer;
export const DeliveryAction = {
  YES: "YES",
  NO: "NO",
};
const PATH = "components/InAppRTOModal";

const RTOModal = ({
  source = "",
  rtoFeedbackModal,
  closeRTOModal = () => {},
  actionData,
  checkFromFPN = false,
}) => {
  const [visible, setVisible] = useState(false);
  const dispatch = useDispatch();
  const { deliveryFlowEnabled = false, deliveryFlowInfo } =
    useSelector(deliveryFlowInfoSelector) || {};
  const { orders = [], ordersCount = 0, meta = {} } = deliveryFlowInfo || {};
  const [action, setAction] = useState({
    type: "",
    reasons: "",
    order_ids: "",
  });
  const checkForFeedbackModal = useMemo(
    () => !!rtoFeedbackModal && actionData?.reasons,
    [rtoFeedbackModal, actionData],
  );

  useEffect(() => {
    if (deliveryFlowEnabled && (source === "MyDelivery" || checkFromFPN)) {
      analytics.trackEvent(DELIVERY_COMMUNICATION_EVENTS.DEL_COMM_MODAL_SHOW, {
        order_id: checkForFeedbackModal
          ? action.order_ids.split(",")
          : orders[0]?.order_ids,
        source: source === "MyDelivery" && !checkFromFPN ? "In-App" : "FPN",
      });
    }
  }, [deliveryFlowEnabled, checkFromFPN, source]);

  useEffect(() => {
    if (source === "MyDelivery" || checkFromFPN) {
      updateDeliveryInfoData();
    }
  }, [source]);

  useEffect(() => {
    if (rtoFeedbackModal) {
      actionData && setAction({ ...actionData });
      if (actionData?.reasons) {
        setVisible(true);
      } else {
        markedRTO(actionData?.type);
      }
    }
  }, [rtoFeedbackModal, actionData]);

  const closeModal = () => {
    if (checkForFeedbackModal) {
      closeRTOModal();
    } else {
      dispatch(
        delayDataActions.updateDeliveryFlowInfo({ deliveryFlowEnabled: false }),
      );
    }
  };

  const onCrossButtonPressed = async () => {
    try {
      await confirmOrderDeliveryFlow({
        confirm_order: "ACKNOWLEDGED",
        order_ids: checkForFeedbackModal
          ? action.order_ids.split(",")
          : orders[0]?.order_ids,
      });
      dispatch(
        delayDataActions.updateDeliveryFlowInfo({ deliveryFlowEnabled: false }),
      );
      setVisible(false);
      analytics.trackEvent(DELIVERY_COMMUNICATION_EVENTS.DEL_COMM_BTN_CLOSE, {
        order_id: checkForFeedbackModal
          ? action.order_ids.split(",")
          : orders[0]?.order_ids,
        reason: "ACKNOWLEDGED",
      });
    } catch (error) {
      console.log("error", error);
    }
  };

  const hideFeedbackModal = () => {
    if (checkForFeedbackModal) {
      closeRTOModal();
    }
    setVisible(false);
  };

  const updateDeliveryInfoData = () => {
    dispatch(delayDataActions.getDeliveryFlowInfoData());
  };

  const onPressNeeded = (type = "", reasons = "") => {
    // Check if order is escalated and user is marking RTO
    if (type === DeliveryAction.NO && orders[0]?.is_escalated) {
      analytics.trackEvent("DB_ESCALATED_RTO_WARNING_SHOWN", {
        order_id: orders[0]?.order_ids,
      });
      Alert.alert(
        "",
        "⚠️ कस्टमर को यह ऑर्डर चाहिए। कृपया डिलीवरी की कोशिश करें। क्या आप अभी भी इसे RTO मार्क करना चाहते हैं?",
        [
          {
            text: "Cancel",
            style: "cancel",
            onPress: () => {
              analytics.trackEvent("DB_ESCALATED_RTO_CANCELLED", {
                order_id: orders[0]?.order_ids,
              });
            },
          },
          {
            text: "Yes, Raise CX RTO",
            onPress: () => {
              analytics.trackEvent("DB_ESCALATED_RTO_OVERRIDE", {
                order_id: orders[0]?.order_ids,
              });
              setAction({ type, reasons });
              if (reasons) {
                setVisible(true);
              } else {
                markedRTO(type);
              }
            },
          },
        ],
      );
      return;
    }

    setAction({ type, reasons });
    if (reasons) {
      setVisible(true);
    } else {
      markedRTO(type);
    }
  };

  const markedRTO = async (type) => {
    try {
      const { data } = await confirmOrderDeliveryFlow({
        confirm_order: type,
        order_ids: checkForFeedbackModal
          ? action.order_ids.split(",")
          : orders[0]?.order_ids,
      });
      if (!checkForFeedbackModal && ordersCount > 1) {
        updateDeliveryInfoData();
        setVisible(false);
      } else {
        closeModal();
      }
      if (type === DeliveryAction.YES) {
        showSnackbar(t(`${PATH}/successMessage`));
        analytics.trackEvent(DELIVERY_COMMUNICATION_EVENTS.DEL_COMM_BTN_YES, {
          order_id: checkForFeedbackModal
            ? action.order_ids.split(",")
            : orders[0]?.order_ids,
          reason: "",
        });
      } else {
        showSnackbar(t(`${PATH}/rejectedMessage`));
        analytics.trackEvent(DELIVERY_COMMUNICATION_EVENTS.DEL_COMM_BTN_NO, {
          order_id: checkForFeedbackModal
            ? action.order_ids.split(",")
            : orders[0]?.order_ids,
          reason: "",
        });
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  const showSnackbar = (msg = "") => {
    const snackbarPayload = {
      message: msg,
      autoHide: false,
      actionText: "Okay",
      actionHandler: (e) => {
        dispatch(snackbarActions.hideSnackbar());
      },
    };
    dispatch(snackbarActions.showSnackbar(snackbarPayload));
  };

  const submitFeedback = async (reason = "") => {
    try {
      const { data } = await confirmOrderDeliveryFlow({
        confirm_order: action.type,
        order_ids: checkForFeedbackModal
          ? action.order_ids.split(",")
          : orders[0]?.order_ids,
        reason,
      });
      if (!checkForFeedbackModal && ordersCount > 1) {
        updateDeliveryInfoData();
        setVisible(false);
      } else {
        closeModal();
      }
      if (action.type === DeliveryAction.YES) {
        showSnackbar(t(`${PATH}/successMessage`));
        analytics.trackEvent(DELIVERY_COMMUNICATION_EVENTS.DEL_COMM_BTN_YES, {
          order_id: checkForFeedbackModal
            ? action.order_ids.split(",")
            : orders[0]?.order_ids,
          reason: reason,
        });
      } else {
        showSnackbar(t(`${PATH}/rejectedMessage`));
        analytics.trackEvent(DELIVERY_COMMUNICATION_EVENTS.DEL_COMM_BTN_NO, {
          order_id: checkForFeedbackModal
            ? action.order_ids.split(",")
            : orders[0]?.order_ids,
          reason: reason,
        });
      }
    } catch (error) {
      console.log("error", error);
    }
  };

  return (
    <Modal
      statusBarTranslucent
      navigationBarTranslucent
      animationType="fade"
      transparent={true}
      visible={deliveryFlowEnabled || (checkForFeedbackModal && visible)}
      onRequestClose={onCrossButtonPressed}
      style={styles.container}
    >
      {visible ? (
        <RTOFeedbackModal
          meta={meta}
          hideFeedbackModal={hideFeedbackModal}
          submitFeedback={submitFeedback}
          action={action}
        />
      ) : (
        <InAppRTOModal
          setVisible={setVisible}
          closeModal={onCrossButtonPressed}
          onPressNeeded={onPressNeeded}
          item={orders[0]}
          ordersCount={ordersCount}
        />
      )}
    </Modal>
  );
};

export default memo(RTOModal);

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
});
